/**
 * EM Portal — Client Feedback page.
 * Displays assessment feedback submitted by clients, scoped to the EM's
 * assigned organizations. Includes rating, comments, domain, and submitter info.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Rating,
  Card, CardContent, Grid, TextField, InputAdornment,
} from '@mui/material';
import { Feedback, Search, Star } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface FeedbackItem {
  id: string;
  assessment_id: string;
  assessment_domain: string;
  organization_name: string;
  submitted_by: string;
  submitted_by_email: string;
  rating: number;
  comments: string | null;
  created_at: string | null;
}

const RATING_LABELS: Record<number, string> = {
  1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very Good', 5: 'Excellent',
};

const EMClientFeedback: React.FC = () => {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/sme/assessment-feedback');
      const data = await res.json();
      if (data.success) {
        setFeedback(data.feedback);
        setLastRefreshed(new Date());
      } else {
        setError(data.error || 'Failed to load feedback');
      }
    } catch {
      setError('Failed to load client feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  // Compute summary stats
  const totalFeedback = feedback.length;
  const avgRating = totalFeedback > 0
    ? feedback.reduce((sum, f) => sum + f.rating, 0) / totalFeedback
    : 0;
  const ratingDistribution = [1, 2, 3, 4, 5].map(
    (r) => feedback.filter((f) => f.rating === r).length,
  );

  // Filter by search term
  const filtered = feedback.filter((f) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      f.assessment_domain.toLowerCase().includes(term) ||
      f.organization_name.toLowerCase().includes(term) ||
      f.submitted_by.toLowerCase().includes(term) ||
      (f.comments || '').toLowerCase().includes(term)
    );
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Feedback color="primary" />
          <Typography variant="h5" fontWeight={600}>Client Feedback</Typography>
        </Box>
        <RefreshButton onRefresh={fetchFeedback} lastRefreshed={lastRefreshed} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">Total Responses</Typography>
              <Typography variant="h4" fontWeight={700} color="primary">{totalFeedback}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">Average Rating</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {avgRating ? avgRating.toFixed(1) : '—'}
                </Typography>
                <Star sx={{ color: '#faaf00', fontSize: 28 }} />
              </Box>
              {avgRating > 0 && (
                <Rating value={avgRating} precision={0.1} readOnly size="small" sx={{ mt: 0.5 }} />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">Rating Distribution</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                {ratingDistribution.map((count, idx) => (
                  <Chip
                    key={idx + 1}
                    label={`${idx + 1}★ (${count})`}
                    size="small"
                    variant={count > 0 ? 'filled' : 'outlined'}
                    color={idx >= 3 ? 'success' : idx === 2 ? 'info' : 'warning'}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search by domain, organization, client, or comments..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, width: '100%', maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Alert severity="info">
          {searchTerm ? 'No feedback matches your search.' : 'No client feedback received yet.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, backgroundColor: '#f5f5f5' } }}>
                <TableCell>Assessment Domain</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Client</TableCell>
                <TableCell align="center">Rating</TableCell>
                <TableCell>Comments</TableCell>
                <TableCell>Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((fb) => (
                <TableRow key={fb.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{fb.assessment_domain}</Typography>
                  </TableCell>
                  <TableCell>{fb.organization_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{fb.submitted_by}</Typography>
                    <Typography variant="caption" color="text.secondary">{fb.submitted_by_email}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Rating value={fb.rating} readOnly size="small" />
                      <Chip
                        label={RATING_LABELS[fb.rating] || fb.rating}
                        size="small"
                        color={fb.rating >= 4 ? 'success' : fb.rating === 3 ? 'info' : 'warning'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300, whiteSpace: 'pre-wrap' }}>
                      {fb.comments || <em style={{ color: '#999' }}>No comments</em>}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {fb.created_at
                        ? new Date(fb.created_at).toLocaleDateString(undefined, {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })
                        : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default EMClientFeedback;
